import React, { useEffect, useState } from 'react';

import billingApiClient from 'api/billingApiClient';
import closeIcon from 'assets/filters/close.svg';
import VectorIcon from 'assets/myLeads/vector.svg';
import Button from 'common-ui/button';
import CustomDatePicker from 'common-ui/customDatePicker';
import Input from 'common-ui/input';
import { AsyncSelect, Select } from 'common-ui/select';
import dayjs from 'dayjs';
import useDebounce from 'hooks/useDebounce';
import { generateOptions } from 'utils';

import { STATUS_OPTIONS, URGENCY_OPTIONS } from '../MyAppointmentsConstants';
import {
  ClearAllRow,
  ClearAllText,
  CloseIconWrapper,
  FilterContainer,
  FilterLabel,
  FilterRow,
  FilterRowLine,
  Icon,
  InputWrapper,
  Line,
  MainActionsRow,
} from './Toolbar.styles';
import { useMyAppointmentsSearchParams } from './useSearchData';

const getSearchAndFilterCount = (searchData) => {
  const searchKeys = ['customerName', 'communicationMethod', 'customerAddress'];
  const filterKeys = ['status', 'offer', 'urgency'];
  const dateKeys = ['fromDate', 'toDate'];

  const searchCount = searchKeys.reduce((count, key) => {
    const value = searchData[key];
    return count + (typeof value === 'string' && value.trim() !== '' ? 1 : 0);
  }, 0);

  const filterCount = [
    ...filterKeys.map((key) => searchData[key]),
    ...dateKeys.map((key) => searchData[key]),
  ].reduce((count, value) => {
    if (value === null || value === undefined) return count;
    if (typeof value === 'string') return count + (value.trim() !== '' ? 1 : 0);
    return count + 1;
  }, 0);

  return { searchCount, filterCount };
};

const Toolbar = () => {
  const [showFilters, setShowFilters] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const { searchData, setSearchData, resetSearchData } = useMyAppointmentsSearchParams();
  const { searchCount, filterCount } = getSearchAndFilterCount(searchData);

  const [localSearch, setLocalSearch] = useState({
    customerName: searchData.customerName || '',
    communicationMethod: searchData.communicationMethod || '',
    customerAddress: searchData.customerAddress || '',
  });

  const [offerOptions, setOfferOptions] = useState([]);
  const [first, setFirst] = useState(false);

  const fetchOfferOptions = async (searchTerm) => {
    try {
      const url = searchTerm
        ? `/tariff/active?name=${encodeURIComponent(searchTerm)}`
        : `/tariff/active`;
      const response = await billingApiClient.get(url);
      const options = generateOptions(response.data);
      setOfferOptions(options);
      return options;
    } catch (error) {
      console.error('Error fetching offer options:', error);
    }
  };

  useEffect(() => {
    if (searchData.offer) {
      fetchOfferOptions();
    }
  }, []);

  const debouncedCustomerName = useDebounce(localSearch.customerName, 500);
  const debouncedCommunicationMethod = useDebounce(localSearch.communicationMethod, 500);
  const debouncedCustomerAddress = useDebounce(localSearch.customerAddress, 500);

  useEffect(() => {
    if (first) {
      setSearchData({
        customerName: debouncedCustomerName,
        communicationMethod: debouncedCommunicationMethod,
        customerAddress: debouncedCustomerAddress,
      });
    }
  }, [debouncedCustomerName, debouncedCommunicationMethod, debouncedCustomerAddress]);

  const handleSearchInputChange = (key, value) => {
    setFirst(true);
    setLocalSearch((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    resetSearchData();
    setLocalSearch({ customerName: '', communicationMethod: '', customerAddress: '' });
    setFirst(false);
  };

  const handleCloseFilters = () => {
    setShowFilters(false);
  };

  return (
    <FilterContainer>
      <MainActionsRow>
        <div className="actions-left">
          <Button
            outlined
            onClick={() => {
              setShowSearch(!showSearch);
              setShowFilters(false);
            }}
            className="action-button"
            active={searchCount > 0}
            variant="search"
          >
            Search{searchCount > 0 ? ` (${searchCount})` : ''}
          </Button>
          <Button
            outlined
            onClick={() => {
              setShowFilters(!showFilters);
              setShowSearch(false);
            }}
            className="action-button"
            active={filterCount > 0}
            variant="filter"
          >
            Filter{filterCount > 0 ? ` (${filterCount})` : ''}
          </Button>
        </div>
      </MainActionsRow>

      {showSearch && (
        <Line>
          <ClearAllRow className="close-x">
            <CloseIconWrapper onClick={() => setShowSearch(false)}>
              <Icon className="close-x-icon" src={closeIcon} />
            </CloseIconWrapper>
          </ClearAllRow>
          <FilterRow>
            <InputWrapper>
              <Input
                className="search-inputs"
                label="Full Name"
                icon={
                  focusedField === 'customerName' || localSearch.customerName
                    ? null
                    : VectorIcon
                }
                value={localSearch.customerName}
                onFocus={() => setFocusedField('customerName')}
                onBlur={() => setFocusedField(null)}
                onChange={(e) => handleSearchInputChange('customerName', e.target.value)}
              />
              <Input
                className="search-inputs"
                label="Communication Method"
                icon={
                  focusedField === 'communicationMethod' || localSearch.communicationMethod
                    ? null
                    : VectorIcon
                }
                value={localSearch.communicationMethod}
                onFocus={() => setFocusedField('communicationMethod')}
                onBlur={() => setFocusedField(null)}
                onChange={(e) =>
                  handleSearchInputChange('communicationMethod', e.target.value)
                }
              />
              <Input
                className="search-inputs"
                label="Address"
                icon={
                  focusedField === 'customerAddress' || localSearch.customerAddress
                    ? null
                    : VectorIcon
                }
                value={localSearch.customerAddress}
                onFocus={() => setFocusedField('customerAddress')}
                onBlur={() => setFocusedField(null)}
                onChange={(e) => handleSearchInputChange('customerAddress', e.target.value)}
              />
            </InputWrapper>
          </FilterRow>
        </Line>
      )}

      {showFilters && (
        <Line>
          <ClearAllRow className="close-x">
            <CloseIconWrapper onClick={handleCloseFilters}>
              <Icon className="close-x-icon" src={closeIcon} />
            </CloseIconWrapper>
          </ClearAllRow>
          <FilterRow>
            <FilterRowLine>
              <div>
                <Select
                  options={STATUS_OPTIONS}
                  placeholder="Status"
                  isClearable={true}
                  value={STATUS_OPTIONS.find((opt) => opt.value === searchData.status) || null}
                  onChange={(val) => setSearchData({ status: val?.value ?? '' })}
                />
              </div>
              <div className="date-group">
                <FilterLabel>Created Date</FilterLabel>
                <div className="date-inputs">
                  <CustomDatePicker
                    placeholder="From"
                    value={searchData.fromDate || null}
                    onChange={(date) =>
                      setSearchData({
                        fromDate: date ? dayjs(date).format('YYYY-MM-DD') : '',
                      })
                    }
                    disableFuture
                    maxDate={searchData.toDate ? dayjs(searchData.toDate) : null}
                  />
                  <CustomDatePicker
                    placeholder="To"
                    value={searchData.toDate || null}
                    onChange={(date) =>
                      setSearchData({
                        toDate: date ? dayjs(date).format('YYYY-MM-DD') : '',
                      })
                    }
                    disableFuture
                    minDate={searchData.fromDate ? dayjs(searchData.fromDate) : null}
                  />
                </div>
              </div>
              <div className="date-inputs">
                <AsyncSelect
                  placeholder="Offer"
                  defaultOptions={offerOptions}
                  loadOptions={fetchOfferOptions}
                  onMenuOpen={fetchOfferOptions}
                  onInputChange={fetchOfferOptions}
                  value={offerOptions.find((opt) => opt.value === searchData.offer) || null}
                  onChange={(val) => setSearchData({ offer: val?.value ?? '' })}
                  isClearable
                  isSearchable
                />
              </div>
              <div>
                <Select
                  options={URGENCY_OPTIONS}
                  placeholder="Urgency"
                  isClearable={true}
                  value={
                    URGENCY_OPTIONS.find((opt) => opt.value === searchData.urgency) || null
                  }
                  onChange={(val) => setSearchData({ urgency: val?.value ?? '' })}
                />
              </div>
            </FilterRowLine>
          </FilterRow>

          <ClearAllRow>
            <ClearAllText onClick={handleReset}>Clear all</ClearAllText>
          </ClearAllRow>
        </Line>
      )}
    </FilterContainer>
  );
};

export default Toolbar;
